import { useEffect, useState, useCallback } from "react";
import { Icon, EmptyState } from "../../components/Common.jsx";
import { useApp } from "../../store.jsx";
import { fmtRelative, fmtDateTime } from "../../lib/dates.js";
import TableSkeleton from "../../components/admin/TableSkeleton.jsx";
import AdminPageBoundary from "../../components/admin/AdminPageBoundary.jsx";
import SupportChat from "../../components/admin/SupportChat.jsx";
import { listSupportTickets, updateSupportTicket, getSupportTicket } from "../../lib/adminApi";

const STATUS_OPTIONS = [
  { value: "", label: "All" },
  { value: "open", label: "Open" },
  { value: "in_progress", label: "In Progress" },
  { value: "resolved", label: "Resolved" },
  { value: "closed", label: "Closed" },
];

const SOURCE_OPTIONS = [
  { value: "", label: "All Sources" },
  { value: "chatbot", label: "Chatbot" },
  { value: "landing_page", label: "Landing Page" },
];

const statusBadge = (s) => {
  const map = {
    open: "badge-error",
    in_progress: "badge-info",
    resolved: "badge-success",
    closed: "badge-muted",
  };
  const label = (s || "").replace("_", " ");
  return <span className={`badge ${map[s] || "badge-muted"}`}>{label}</span>;
};

function SupportInner() {
  const { toast } = useApp();
  const [tickets, setTickets] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState("");
  const [source, setSource] = useState("");
  const [page, setPage] = useState(1);
  const [selected, setSelected] = useState(null);
  const [notes, setNotes] = useState("");
  const [newStatus, setNewStatus] = useState("");
  const [saving, setSaving] = useState(false);

  const limit = 20;

  const load = useCallback(() => {
    setLoading(true);
    const params = new URLSearchParams({ page, limit });
    if (status) params.set("status", status);
    if (source) params.set("source", source);
    listSupportTickets(`?${params}`)
      .then((d) => { setTickets(d.tickets || []); setTotal(d.total || 0); })
      .catch((e) => toast(e.message || "Failed to load tickets", "error"))
      .finally(() => setLoading(false));
  }, [page, status, source, toast]);

  useEffect(() => { load(); }, [load]);

  const openDetail = async (ticket) => {
    try {
      const full = await getSupportTicket(ticket.id);
      setSelected(full);
      setNotes(full.notes || "");
      setNewStatus(full.status);
    } catch (e) {
      toast(e.message || "Failed to load ticket", "error");
    }
  };

  const closeDetail = () => setSelected(null);

  const saveTicket = async () => {
    if (!selected) return;
    setSaving(true);
    try {
      const updated = await updateSupportTicket(selected.id, { status: newStatus, notes });
      setTickets((t) => t.map((x) => (x.id === updated.id ? { ...x, ...updated } : x)));
      setSelected((s) => ({ ...s, ...updated }));
      toast("Ticket updated", "success");
    } catch (e) {
      toast(e.message || "Failed to update", "error");
    } finally {
      setSaving(false);
    }
  };

  const totalPages = Math.ceil(total / limit);

  return (
    <section>
      <div className="page-head">
        <div>
          <h1 className="page-title">Support Tickets</h1>
          <p className="page-sub">{total} ticket{total !== 1 ? "s" : ""}</p>
        </div>
      </div>

      <div className="card" style={{ marginBottom: 14 }}>
        <div style={{ display: "flex", gap: 10, flexWrap: "wrap", alignItems: "center" }}>
          {STATUS_OPTIONS.map((o) => (
            <button
              key={o.value}
              className={`filter-chip ${status === o.value ? "active" : ""}`}
              onClick={() => { setStatus(o.value); setPage(1); }}
            >
              {o.label}
            </button>
          ))}
          <select
            className="input"
            value={source}
            onChange={(e) => { setSource(e.target.value); setPage(1); }}
            style={{ fontSize: 12, padding: "5px 10px", height: 32 }}
          >
            {SOURCE_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </select>
        </div>
      </div>

      {loading ? (
        <TableSkeleton columns={6} rows={6} />
      ) : tickets.length === 0 ? (
        <EmptyState title="No tickets" desc="No support tickets found." icon="headset" />
      ) : (
        <>
          <div className="table-wrap">
            <table className="tbl">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Email</th>
                  <th>Phone</th>
                  <th>Status</th>
                  <th>Source</th>
                  <th>Messages</th>
                  <th>Date</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {tickets.map((t) => (
                  <tr key={t.id} style={{ cursor: "pointer" }} onClick={() => openDetail(t)}>
                    <td data-label="Name" style={{ fontWeight: 600 }}>{t.name}</td>
                    <td data-label="Email" style={{ fontSize: 13, color: "var(--text-muted)" }}>{t.email || "—"}</td>
                    <td data-label="Phone" style={{ fontSize: 13, color: "var(--text-muted)" }}>{t.phone || "—"}</td>
                    <td data-label="Status">{statusBadge(t.status)}</td>
                    <td data-label="Source">
                      <span className="badge badge-muted">{(t.source || "").replace("_", " ")}</span>
                    </td>
                    <td data-label="Messages" style={{ fontSize: 12 }}>{t._count?.messages ?? 0}</td>
                    <td data-label="Date" style={{ fontSize: 12, color: "var(--text-muted)" }}>
                      {fmtRelative(t.createdAt)}
                    </td>
                    <td>
                      <button className="icon-btn" title="View details">
                        <Icon name="chevron-right" size={14} />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {totalPages > 1 && (
            <div style={{ display: "flex", justifyContent: "center", gap: 8, marginTop: 14 }}>
              <button className="btn btn-ghost" disabled={page <= 1} onClick={() => setPage(page - 1)}>
                Prev
              </button>
              <span style={{ fontSize: 13, color: "var(--text-muted)", alignSelf: "center" }}>
                Page {page} of {totalPages}
              </span>
              <button className="btn btn-ghost" disabled={page >= totalPages} onClick={() => setPage(page + 1)}>
                Next
              </button>
            </div>
          )}
        </>
      )}

      {selected && (
        <>
          <div
            onClick={closeDetail}
            style={{
              position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)",
              zIndex: 900, cursor: "pointer",
            }}
          />
          <div
            style={{
              position: "fixed", top: "50%", left: "50%",
              transform: "translate(-50%, -50%)",
              zIndex: 901, width: "92%", maxWidth: 720,
              background: "var(--surface)", border: "1px solid var(--border)",
              borderRadius: 16, padding: 22, maxHeight: "88vh", overflowY: "auto",
              boxShadow: "0 8px 32px rgba(0,0,0,0.3)",
            }}
          >
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
              <h2 style={{ margin: 0, fontSize: 18 }}>Ticket Details</h2>
              <button className="icon-btn" onClick={closeDetail}>
                <Icon name="x" size={16} />
              </button>
            </div>

            <div style={{ display: "grid", gap: 8, marginBottom: 16, gridTemplateColumns: "1fr 1fr" }}>
              <Row label="Name" value={selected.name} />
              <Row label="Email" value={selected.email || "—"} />
              <Row label="Phone" value={selected.phone || "—"} />
              <Row label="Source" value={(selected.source || "").replace("_", " ")} />
              <Row label="Company" value={selected.company?.name || "—"} />
              <Row label="Reporter" value={selected.user?.name || selected.user?.email || "—"} />
              <Row label="Created" value={fmtDateTime(selected.createdAt)} />
            </div>

            <div style={{ marginBottom: 16 }}>
              <label style={{ fontSize: 12, fontWeight: 700, color: "var(--text-muted)", textTransform: "uppercase", letterSpacing: 1 }}>
                Original message
              </label>
              <div style={{
                marginTop: 4, padding: 12, borderRadius: 8,
                background: "var(--surface-2, rgba(148,163,184,0.12))", fontSize: 13,
                lineHeight: 1.5, whiteSpace: "pre-wrap", color: "var(--text)",
              }}>
                {selected.message}
              </div>
            </div>

            <SupportChat
              ticket={selected}
              locked={selected.status === "closed"}
              onTicketUpdated={() => {
                if (selected.status === "open") setNewStatus("in_progress");
              }}
            />

            <div style={{ marginTop: 16, marginBottom: 12 }}>
              <label style={{ fontSize: 12, fontWeight: 700, color: "var(--text-muted)", textTransform: "uppercase", letterSpacing: 1 }}>
                Status
              </label>
              <select
                className="input"
                value={newStatus}
                onChange={(e) => setNewStatus(e.target.value)}
                style={{
                  display: "block", width: "100%", marginTop: 4,
                  padding: "8px 10px", fontSize: 13,
                }}
              >
                {STATUS_OPTIONS.filter((o) => o.value).map((o) => (
                  <option key={o.value} value={o.value}>{o.label}</option>
                ))}
              </select>
            </div>

            <div style={{ marginBottom: 16 }}>
              <label style={{ fontSize: 12, fontWeight: 700, color: "var(--text-muted)", textTransform: "uppercase", letterSpacing: 1 }}>
                Admin Notes
              </label>
              <textarea
                className="input"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                rows={3}
                placeholder="Internal notes (not visible to customer)"
                style={{ display: "block", width: "100%", marginTop: 4, fontSize: 13 }}
              />
            </div>

            <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
              <button className="btn btn-ghost" onClick={closeDetail}>Close</button>
              <button className="btn btn-primary" onClick={saveTicket} disabled={saving}>
                {saving ? "Saving…" : "Save status & notes"}
              </button>
            </div>
          </div>
        </>
      )}
    </section>
  );
}

function Row({ label, value }) {
  return (
    <div style={{ display: "flex", gap: 8 }}>
      <span style={{ fontSize: 12, fontWeight: 600, color: "var(--text-muted)", minWidth: 70 }}>{label}:</span>
      <span style={{ fontSize: 13, color: "var(--text)" }}>{value}</span>
    </div>
  );
}

export default function AdminSupport() {
  return (
    <AdminPageBoundary>
      <SupportInner />
    </AdminPageBoundary>
  );
}
